import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../API.js';
import { useAuth } from '../../providers/AuthContext';
import EmailAuthCreateUser from '../../components/EmailAuthCreateUser';
import EmailauthDelegationDialog from './EmailauthDelegationDialog';
import styles from './UsersManagement.module.css';

const DEFAULT_PAGE_SIZE = 25;

function safeBool(x) {
    return !!x;
}

export default function UsersManagement() {
    const { user } = useAuth();
    const [pageSize] = useState(DEFAULT_PAGE_SIZE);
    const [offset, setOffset] = useState(0);
    const [sort, setSort] = useState('lastName');
    const [query, setQuery] = useState('');
    const [queryDebounced, setQueryDebounced] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [users, setUsers] = useState([]);
    const [totalCount, setTotalCount] = useState(null);

    const [emailauthInstalled, setEmailauthInstalled] = useState(false);
    const [emailauthPerms, setEmailauthPerms] = useState(null); // { canCreatePasswordlessUsers }
    const [emailauthError, setEmailauthError] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [createdUserLink, setCreatedUserLink] = useState('');
    const [delegationOpen, setDelegationOpen] = useState(false);

    const createButtonRef = useRef(null);
    const delegationButtonRef = useRef(null);
    const dialogRef = useRef(null);

    const canSee = !!user?.admin;

    const page = useMemo(() => Math.floor(offset / pageSize) + 1, [offset, pageSize]);
    const hasPrev = offset > 0;
    const hasNext = queryDebounced
        ? users.length === pageSize
        : (users.length === pageSize && (totalCount == null || offset + pageSize < totalCount));
    const showingRange = useMemo(() => {
        if (users.length === 0) return null;
        const start = offset + 1;
        const end = offset + users.length;
        return { start, end };
    }, [offset, users.length]);

    useEffect(() => {
        const trimmed = query.trim();
        const id = setTimeout(() => setQueryDebounced(trimmed), 350);
        return () => clearTimeout(id);
    }, [query]);

    useEffect(() => {
        setOffset(0);
    }, [queryDebounced, sort]);

    useEffect(() => {
        if (!canSee) return;
        let cancelled = false;
        setLoading(true);
        setError('');
        const req = queryDebounced
            ? api.get('network/users', {
                query: {
                    text: queryDebounced,
                    limit: pageSize,
                    offset,
                    sort,
                    sortdir: 1,
                    excludeSystemUsers: true,
                },
            })
            : api.get('network/users', {
                query: {
                    limit: pageSize,
                    offset,
                    sort,
                    sortdir: 1,
                    excludeSystemUsers: true,
                },
            });

        req
            .then((resp) => {
                if (cancelled) return;
                const data = resp.data;
                if (resp.status !== 200 || !Array.isArray(data)) {
                    setUsers([]);
                    setTotalCount(null);
                    setError('Could not load users.');
                    return;
                }
                setUsers(data);
                const rawCount = resp.headers?.['girder-total-count'];
                const parsed = rawCount != null ? parseInt(rawCount, 10) : NaN;
                setTotalCount(Number.isFinite(parsed) ? parsed : null);
            })
            .catch(() => {
                if (cancelled) return;
                setUsers([]);
                setTotalCount(null);
                setError('Could not load users.');
            })
            .finally(() => {
                if (cancelled) return;
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [canSee, pageSize, offset, sort, queryDebounced]);

    useEffect(() => {
        if (!canSee) return;
        let cancelled = false;
        setEmailauthError('');
        api.get('emailauth/capabilities')
            .then((resp) => {
                if (cancelled) return;
                setEmailauthInstalled(resp.status === 200);
            })
            .catch(() => {
                if (cancelled) return;
                setEmailauthInstalled(false);
            });
        api.get('emailauth/permissions')
            .then((resp) => {
                if (cancelled) return;
                if (resp.status !== 200 || !resp.data) {
                    setEmailauthPerms(null);
                    return;
                }
                setEmailauthPerms({ canCreatePasswordlessUsers: safeBool(resp.data.canCreatePasswordlessUsers) });
            })
            .catch(() => {
                if (cancelled) return;
                setEmailauthPerms(null);
                setEmailauthError('Could not load emailauth permissions.');
            });
        return () => {
            cancelled = true;
        };
    }, [canSee]);

    const onCreated = useCallback((u) => {
        // Refresh list and jump to first page so the new user is visible.
        setOffset(0);
        if (u?._id) {
            setCreatedUserLink(`/dsa/#useraccount/${u._id}/info`);
        }
    }, []);

    const closeCreate = useCallback(() => {
        setCreateOpen(false);
        requestAnimationFrame(() => createButtonRef.current?.focus());
    }, []);

    const closeDelegation = useCallback(() => {
        setDelegationOpen(false);
        requestAnimationFrame(() => delegationButtonRef.current?.focus());
    }, []);

    useEffect(() => {
        if (!createOpen && !delegationOpen) return undefined;
        function onKeyDown(e) {
            if (e.key === 'Escape') {
                e.preventDefault();
                if (delegationOpen) closeDelegation();
                else closeCreate();
            }
        }
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [createOpen, delegationOpen, closeCreate, closeDelegation]);

    useEffect(() => {
        if (!createOpen) return undefined;
        const id = requestAnimationFrame(() => {
            const root = dialogRef.current;
            const first = root?.querySelector('input, button:not([disabled])');
            first?.focus();
        });
        return () => cancelAnimationFrame(id);
    }, [createOpen]);

    if (!canSee) {
        return <div><h2>Please log in as an administrator to view this page</h2></div>;
    }

    return (
        <div className={styles.page}>
            <div className={styles.headerRow}>
                <h1>Users</h1>
                <div className={styles.headerActions}>
                    <button
                        type="button"
                        ref={delegationButtonRef}
                        className={styles.secondaryButton}
                        onClick={() => setDelegationOpen(true)}
                    >
                        Delegation
                    </button>
                    <button
                        type="button"
                        ref={createButtonRef}
                        className={styles.primaryButton}
                        onClick={() => { setCreatedUserLink(''); setCreateOpen(true); }}
                    >
                        Create user
                    </button>
                </div>
            </div>

            <section className={styles.panel}>
                <div className={styles.panelTitleRow}>
                    <h2>All users</h2>
                    <div className={styles.muted}>
                        {totalCount != null ? `Total: ${totalCount}` : null}
                    </div>
                </div>

                <div className={styles.toolbarRow}>
                    <input
                        className={styles.searchInput}
                        placeholder="Search users…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    <label>
                        Sort by{' '}
                        <select value={sort} onChange={(e) => setSort(e.target.value)}>
                            <option value="lastName">Last name</option>
                            <option value="login">Login</option>
                            <option value="email">Email</option>
                        </select>
                    </label>
                    <div className={styles.muted} style={{ marginLeft: 'auto' }}>
                        Page {page}
                    </div>
                </div>

                {error ? <p role="alert">{error}</p> : null}
                {loading ? <p aria-live="polite">Loading…</p> : null}

                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Login</th>
                                <th>Admin</th>
                                <th>Manage</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => (
                                <tr key={u._id} className={styles.row}>
                                    <td>{u.firstName} {u.lastName}</td>
                                    <td>{u.email}</td>
                                    <td>{u.login}</td>
                                    <td>
                                        <span className={u.admin ? styles.pillYes : styles.pillNo}>
                                            {u.admin ? 'Yes' : 'No'}
                                        </span>
                                    </td>
                                    <td>
                                        <a className={styles.actionLink} href={`/dsa/#useraccount/${u._id}/info`}>
                                            Open in DSA
                                        </a>
                                    </td>
                                </tr>
                            ))}
                            {!loading && users.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className={styles.muted}>
                                        No users found.
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>

                <div className={styles.pagerRow}>
                    <div className={styles.muted}>
                        {showingRange && totalCount != null
                            ? `Showing ${showingRange.start}–${showingRange.end} of ${totalCount}`
                            : null}
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <button
                            type="button"
                            className={styles.secondaryButton}
                            onClick={() => setOffset(Math.max(0, offset - pageSize))}
                            disabled={!hasPrev || loading}
                        >
                            Previous
                        </button>
                        <button
                            type="button"
                            className={styles.secondaryButton}
                            onClick={() => setOffset(offset + pageSize)}
                            disabled={!hasNext || loading}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </section>

            {createOpen ? (
                <div
                    className={styles.overlay}
                    role="presentation"
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) closeCreate();
                    }}
                >
                    <div
                        className={styles.dialog}
                        ref={dialogRef}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="admin-users-create-title"
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <div className={styles.dialogTitleRow}>
                            <h2 className={styles.dialogTitle} id="admin-users-create-title">
                                Create user
                            </h2>
                        </div>

                        {emailauthError ? <p role="alert">{emailauthError}</p> : null}
                        {!emailauthInstalled ? (
                            <p className={styles.muted}>girder-emailauth is not installed.</p>
                        ) : !emailauthPerms?.canCreatePasswordlessUsers ? (
                            <p className={styles.muted}>Ask your site administrator for privileges.</p>
                        ) : (
                            <>
                                <EmailAuthCreateUser
                                    onUserResolved={(u) => {
                                        onCreated(u);
                                    }}
                                />
                                {createdUserLink ? (
                                    <p style={{ marginTop: 12 }}>
                                        <a className={styles.actionLink} href={createdUserLink}>
                                            Manage this user in DSA
                                        </a>
                                    </p>
                                ) : null}
                            </>
                        )}

                        <div className={styles.dialogFooter}>
                            <button
                                type="button"
                                className={styles.secondaryButton}
                                onClick={closeCreate}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            <EmailauthDelegationDialog
                open={delegationOpen}
                onClose={closeDelegation}
            />
        </div>
    );
}

